import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import SiteTable from '../../Site/SiteTable/SiteTable';
import { useContext, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import DeselectionReasonDialog from '../Filters/Summary/DeselectionReasonDialog';
import { SessionContext } from '../../Contexts/SessionContext';
import { SurveyViewModel } from '../../Contexts/SessionState';
import { useTripRateContext } from '../TripRateContext';
import { getFilteredSurveysForSession } from '../../TripRateNew/TriprateFilters';
import SiteFilterViewModel from '../ViewModels/SiteFilterViewModel';
import { surveyRefsMatch } from '../TripRateSurveyRefs';

export default function SiteSelectionTab() {
    const { sessionState } = useContext(SessionContext);
    const { surveys } = useTripRateContext();
    const [searchParams] = useSearchParams();
    const preserveLoadedSelection = searchParams.get("fromSession") === "true" || searchParams.get("fromResults") === "true";
    const selectedSurveyReferenceKey = preserveLoadedSelection
        ? JSON.stringify(Array.from(new Set(sessionState.selectedSitesAndSurveys.map(selection => selection.surveyRef))).sort())
        : '[]';

    const [deselectionRefs, setDeselectionRefs] = useState<string[] | null>(null);
    const [allDeselected, setAllDeselected] = useState<boolean>(false);
    const [multiDeselect, setMultiDeselect] = useState<boolean>(false);
    const closeDeselectionDialog = () => setDeselectionRefs(null);

    const filteredSiteFilter = useMemo(() => {
        const filter = new SiteFilterViewModel();
        const filteredReferences = getFilteredSurveysForSession(surveys, sessionState).map(s => s.surveyRef);
        const selectedSurveyReferences = JSON.parse(selectedSurveyReferenceKey) as string[];
        filter.references = preserveLoadedSelection && selectedSurveyReferences.length !== 0
            ? filteredReferences.filter(reference => selectedSurveyReferences.some(savedReference =>
                surveyRefsMatch(savedReference, reference)))
            : filteredReferences;
        return filter;
    }, [
        surveys,
        sessionState.primaryFilters,
        sessionState.secondaryFilters,
        sessionState.residentialUnitTypes,
        sessionState.landusePage,
        selectedSurveyReferenceKey,
        preserveLoadedSelection
    ]);

    const handleSingleDeselection = (ref: string) => {
        let refs = deselectionRefs ?? [];
        const i = refs.indexOf(ref);
        if (i > 0) {
            refs.splice(i, 1);
        } else {
            refs.push(ref);
        }
        setDeselectionRefs(refs);
    };

    const toggleMultiDeselect = () => {
        setMultiDeselect(!multiDeselect);
    }

    const toggleAllSurveys = () => {
        setAllDeselected(!allDeselected);

        const selected: SurveyViewModel[] = sessionState?.selectedSitesAndSurveys ?? [];
        if (!selected.length) return;

        const allRefs = Array.from(
            new Set(
                selected
                    .map(s => s?.surveyRef?.split('|')[0]?.trim())
                    .filter((r): r is string => Boolean(r))
            )
        );
        if (!allRefs.length) return;

        if (!allDeselected) {
            setDeselectionRefs(allRefs);
        } else {
            const updated = selected.map(site => ({
                ...site,
                siteActive: true,
                surveyActive: true,
                reasonForSiteRemoval: '',
                reasonForSurveyRemoval: '',
                manuallyRemoved: false
            }));

            sessionState.selectedSitesAndSurveys = updated;
        }
    };


    return (
        <Box>
            <Grid container>
                <Grid size={12}>
                    <SiteTable type="summaryFiltering" filter={filteredSiteFilter} setDeselectionOpen={handleSingleDeselection} multiDeselect={multiDeselect} />
                </Grid>

                <Grid size={6} />

                <Grid size={3}>
                    <Button onClick={toggleMultiDeselect} disabled={!sessionState.selectedSitesAndSurveys.some(s => s.sitePendingDeselect && s.siteActive) && multiDeselect} fullWidth variant={multiDeselect ? "contained" : "outlined"}>
                        {multiDeselect ? "Confirm Deselection" : "Deselect Multiple"}
                    </Button>
                </Grid>
                <Grid size={3}>
                    <Button onClick={toggleAllSurveys} fullWidth>
                        {allDeselected ? "Select All Sites" : "Deselect All Sites"}
                    </Button>
                </Grid>

                {(deselectionRefs != null && !multiDeselect) && (
                    <DeselectionReasonDialog
                        open={deselectionRefs !== null}
                        onClose={closeDeselectionDialog}
                        references={deselectionRefs}
                        type="site"
                        multiDeselection={multiDeselect}
                    />
                )}
            </Grid >
        </Box>
    );
}
